/**
 * UUID Generator Service
 * Implements business logic for generating UUIDs (v1 and v4)
 */

import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';

export type UuidVersion = 'v1' | 'v4';
export type UuidFormat = 'standard' | 'uppercase' | 'no-hyphens';

export class UuidGeneratorService {
  /**
   * Generate UUID(s)
   */
  static async generate(
    version: UuidVersion = 'v4',
    quantity: number = 1,
    format: UuidFormat = 'standard'
  ): Promise<EncodingOperation> {
    try {
      // Validate quantity
      if (quantity < 1 || quantity > 20) {
        throw new ValidationError(
          'Quantity must be between 1 and 20',
          EncoderToolType.UUID_GENERATOR,
          'Enter a quantity between 1 and 20'
        );
      }

      // Generate UUIDs
      const uuids: string[] = [];
      for (let i = 0; i < quantity; i++) {
        const uuid = version === 'v1' ? this.generateV1() : this.generateV4();
        uuids.push(this.formatUuid(uuid, format));
      }

      const output = quantity === 1 ? uuids[0] : uuids.join('\n');

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.UUID_GENERATOR,
        operationType: 'encode',
        inputData: `${version} (${quantity})`,
        outputData: output,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          version,
          quantity,
          format
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to generate UUID',
        EncoderToolType.UUID_GENERATOR,
        'Please check your settings and try again'
      );
    }
  }

  /**
   * Generate UUID v4 (random)
   */
  private static generateV4(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Generate UUID v1 (timestamp-based)
   */
  private static generateV1(): string {
    const now = Date.now();
    const timestamp = now * 10000 + 0x01B21DD213814000;

    // Low 32 bits (use >>> 0 to ensure unsigned)
    const timeLow = ((timestamp & 0xffffffff) >>> 0).toString(16).padStart(8, '0');

    // Mid 16 bits
    const timeMid = ((timestamp / 0x100000000) & 0xffff).toString(16).padStart(4, '0');

    // High 12 bits with version
    const timeHiAndVersion = (((timestamp / 0x1000000000000) & 0x0fff) | 0x1000).toString(16).padStart(4, '0');

    // Clock sequence (14 bits) and variant
    const clockSeq = ((Math.random() * 0x3fff) | 0x8000).toString(16).padStart(4, '0');

    // Node (48 bits) - random for privacy
    const node = [];
    for (let i = 0; i < 6; i++) {
      node.push(Math.floor(Math.random() * 256).toString(16).padStart(2, '0'));
    }

    return `${timeLow}-${timeMid}-${timeHiAndVersion}-${clockSeq}-${node.join('')}`;
  }

  /**
   * Format UUID according to specified format
   */
  private static formatUuid(uuid: string, format: UuidFormat): string {
    switch (format) {
      case 'uppercase':
        return uuid.toUpperCase();
      case 'no-hyphens':
        return uuid.replace(/-/g, '');
      case 'standard':
      default:
        return uuid.toLowerCase();
    }
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `uuid-generator-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
